/*
  Mije Seoul — custom analytics event layer.
  Loads only when at least one integration is configured (see
  snippets/mije-analytics.liquid). Fans each funnel event out to whichever
  of PostHog / Meta Pixel / GA4 is present; each call is guarded so a
  missing vendor is a no-op, never an error.

  PostHog events emitted HERE: search_performed, wishlist_add,
  notify_me_submitted. That is the whole list.

  The funnel events — product view, add to cart, checkout start — are NOT
  emitted here. They belong to assets/mije-attribution.js, which sends them
  as mije_product_view / mije_add_to_cart / mije_checkout_started with the
  ft_* attribution properties attached. This file used to send a second,
  bare-named copy of each, so PostHog counted every funnel action twice
  (measured in lockstep: 39/39, 5/5, 179/179). Do not add them back.

  Meta Pixel and GA4 are untouched by that: they get their own vendor
  vocabulary from fb()/ga() below, and GA4's names for two of these happen
  to be the strings 'add_to_cart' and 'begin_checkout'. Those are GA4
  events, not PostHog ones.

  Purchase lives in the merchant-pasted web pixel
  (analytics/webpixel/mije-purchase-pixel.js, docs/analytics-setup.md) —
  theme JS dies at checkout.
*/
(function () {
  'use strict';

  if (window.MijeAnalytics) return;
  window.MijeAnalytics = true;

  var ctx = window.__mijeAnalytics || {};
  var currency = ctx.currency || 'SGD';

  function ph(event, props, opts) {
    try {
      if (window.posthog && window.posthog.capture) window.posthog.capture(event, props, opts);
    } catch (e) { /* never break the page */ }
  }

  function fb(event, props) {
    try {
      if (window.fbq) window.fbq('track', event, props);
    } catch (e) {}
  }

  function ga(event, props) {
    try {
      if (window.gtag) window.gtag('event', event, props);
    } catch (e) {}
  }

  function money(n) {
    return Math.round(Number(n) * 100) / 100;
  }

  /* -------------------------------------------------------- page events */

  if (ctx.product) {
    var p = ctx.product;
    /* PostHog product view is NOT emitted here — mije-attribution.js sends
       mije_product_view with the ft_* attribution properties attached. Two
       names for one action made every funnel count double. */
    fb('ViewContent', {
      content_ids: [String(p.id)],
      content_name: p.title,
      content_type: 'product',
      value: money(p.price),
      currency: currency,
    });
    ga('view_item', {
      currency: currency,
      value: money(p.price),
      items: [{ item_id: p.handle, item_name: p.title, price: money(p.price) }],
    });
  }

  if (ctx.search && ctx.search.query) {
    ph('search_performed', {
      query: ctx.search.query,
      results_count: ctx.search.results_count,
    });
    fb('Search', { search_string: ctx.search.query });
    ga('search', { search_term: ctx.search.query });
  }

  /* ------------------------------------------------------- add_to_cart */
  /* mije-cart.js dispatches mije:cart:added with the /cart/add.js
     response JSON after every successful add, from any add path. */

  document.addEventListener('mije:cart:added', function (e) {
    var data = e.detail || {};
    var items = data.items || (data.id ? [data] : []);
    items.forEach(function (item) {
      var price = (item.final_price != null ? item.final_price : item.price) / 100;
      var qty = item.quantity || 1;
      /* PostHog add-to-cart is NOT emitted here — mije-attribution.js sends
         mije_add_to_cart from the same interception points that write the
         cart, so it carries the attribution stamp and cannot drift from it.
         It also de-duplicates the three paths one add can arrive by; this
         listener had no such guard. */
      fb('AddToCart', {
        content_ids: [String(item.product_id || item.id)],
        content_name: item.product_title || item.title,
        content_type: 'product',
        value: money(price * qty),
        currency: currency,
      });
      ga('add_to_cart', {
        currency: currency,
        value: money(price * qty),
        items: [{ item_id: item.handle, item_name: item.product_title || item.title, price: money(price), quantity: qty }],
      });
    });
  });

  /* ---------------------------------------------------- begin_checkout */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.mj-cart__checkout');
    if (!btn) return;
    var value = null;
    var scope = btn.closest('[data-cart-drawer-root]') || btn.closest('[data-cart-page]') || document;
    var subtotalEl = scope.querySelector('.mj-cart__subtotal-value, .mjcp-summary__total-value');
    if (subtotalEl) {
      var n = parseFloat(subtotalEl.textContent.replace(/[^0-9.]/g, ''));
      if (!isNaN(n)) value = n;
    }
    /* PostHog checkout start is NOT emitted here — mije-attribution.js sends
       mije_checkout_started, with the cart total it already tracks and a
       once-per-page guard. NOTE the two ga()/fb() names below are GA4 and
       Meta Pixel events, not PostHog ones: 'begin_checkout' and
       'add_to_cart' are GA4's own recommended-event vocabulary and must
       keep those exact strings. Only the ph() calls were removed. */
    fb('InitiateCheckout', { value: value, currency: currency });
    ga('begin_checkout', { currency: currency, value: value });
  });

  /* ------------------------------------------------------ wishlist_add */
  /* The wishlist store is localStorage-backed (mije-wishlist.js). Snapshot
     on click, compare a tick later: a grown list = an add (not a remove). */

  function wishlistSet() {
    try {
      return new Set(JSON.parse(localStorage.getItem(ctx.wishlistKey || 'mije.wishlist.v1') || '[]'));
    } catch (e) {
      return new Set();
    }
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest(
      '[data-wishlist-toggle], .mj-card__wish, .mj-pdp__wish, .mj-pdp__wish-float, .mj-pdp-bar__wish'
    );
    if (!btn) return;
    var before = wishlistSet();
    setTimeout(function () {
      var after = wishlistSet();
      after.forEach(function (handle) {
        if (!before.has(handle)) {
          ph('wishlist_add', { product_handle: handle });
          fb('AddToWishlist', { content_ids: [handle], content_type: 'product' });
          ga('add_to_wishlist', { items: [{ item_id: handle }] });
        }
      });
    }, 60);
  });

  /* ----------------------------------------------- notify_me_submitted */
  /* The notify modal posts a native customer form and navigates — capture
     with sendBeacon so the event survives the unload.

     Product/variant come off [data-notify-tags], not off ctx.product:
     that input is what mije-wishlist.js fills at open time and what
     Shopify stores verbatim on the customer record, so the event and the
     tag can never disagree about which variant was asked for. ctx.product
     is the fallback and supplies the descriptive fields. See
     docs/notify-me.md for the tag grammar. */

  function notifyTagProps(modal) {
    var input = modal.querySelector('[data-notify-tags]');
    var out = { newsletter_optin: false };
    if (!input || !input.value) return out;
    input.value.split(',').forEach(function (raw) {
      var tag = raw.trim();
      if (tag.indexOf('product-') === 0) out.product_handle = tag.slice(8);
      else if (tag.indexOf('variant-') === 0) out.variant_id = tag.slice(8);
      else if (tag === 'newsletter') out.newsletter_optin = true;
    });
    return out;
  }

  document.addEventListener('submit', function (e) {
    var modal = e.target.closest('[data-notify-modal]');
    if (!modal) return;
    var p = ctx.product || {};
    var t = notifyTagProps(modal);
    ph('notify_me_submitted', {
      product_handle: t.product_handle || p.handle,
      product_id: p.id,
      product_title: p.title,
      variant_id: t.variant_id || p.variant_id,
      price: p.price,
      currency: currency,
      newsletter_optin: t.newsletter_optin,
    }, { transport: 'sendBeacon' });
  });
})();
